import InfoIcon from "@assets/icons/info.svg";
import styled from "@emotion/styled";
import { TippyProps } from "@tippyjs/react";
import { Button, ButtonProps } from "@ui/components/buttons/button";
import { ConditionalSkeleton, ConditionalSkeletonProps } from "@ui/components/common/conditional-skeleton";
import { Tooltip } from "@ui/components/common/tooltip";
import { physioMeTabletBreakpoint, row } from "@ui/style";
import { colors } from "@ui/style/colors";
import React, { forwardRef } from "react";

interface ContentWithTooltipProps {
	text?: string;
	children?: React.ReactNode;
	tooltip?: React.ReactNode | string;
	skeleton?: ConditionalSkeletonProps;
	id?: string;
	tooltipOptions?: TippyProps;
	iconSize?: number;
	iconColor?: string;
}

interface IconContainerProps {
	size?: number;
	color?: string;
}

const IconContainer = forwardRef<HTMLDivElement, IconContainerProps>(
	({ color = colors.grey, size = 20, ...restProps }, ref) => (
		<StyledIconContainer ref={ref} {...restProps}>
			<InfoIcon style={{ cursor: "help", padding: 2 }} width={size} height={size} color={color} />
		</StyledIconContainer>
	),
);

IconContainer.displayName = "IconContainer";

export const ContentWithTooltip: React.FC<ContentWithTooltipProps> = ({
	text,
	children,
	tooltip,
	skeleton,
	tooltipOptions,
	iconSize,
	iconColor,
}) => {
	const showSkeleton = skeleton?.active ?? false;

	return (
		<Container>
			<ConditionalSkeleton {...skeleton} active={showSkeleton}>
				{children ?? <h3 className="p-size-l">{text}</h3>}
			</ConditionalSkeleton>
			{!!tooltip && !showSkeleton && (
				<StyledConditionalSkeleton {...skeleton} active={showSkeleton}>
					<Tooltip content={tooltip} placement="auto" {...tooltipOptions}>
						<IconContainer color={iconColor} size={iconSize} />
					</Tooltip>
				</StyledConditionalSkeleton>
			)}
		</Container>
	);
};

interface TooltipButtonProps extends ButtonProps {
	tooltipContent?: string;
	tooltipPosition?: "auto" | "bottom";
}

export const TooltipButton: React.FC<TooltipButtonProps> = ({
	tooltipContent,
	tooltipPosition = "auto",
	...otherProps
}) => (
	<Tooltip
		content={otherProps.disabled ? tooltipContent : undefined}
		disabled={!otherProps.disabled}
		placement={tooltipPosition}
		maxWidth={400}
	>
		<div>
			<StyledButton {...otherProps} />
		</div>
	</Tooltip>
);

const Container = styled.div`
	${row(8, "flexstart", "center")};
`;

const StyledConditionalSkeleton = styled(ConditionalSkeleton)`
	padding: 0;
`;

const StyledIconContainer = styled.div`
	${row(undefined, "flexstart", "center")};
`;

const StyledButton = styled(Button)`
	@media ${physioMeTabletBreakpoint} {
		width: 100%;
		display: flex;
		justify-content: center;

		> span {
			flex: unset;
		}
	}
`;
